import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import StaffNav from "./managernav";
import { UserIcon, DocumentTextIcon, MapPinIcon, MinusIcon, PlusIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import AppraisalForm from "./appraisalform";
import StaffDetails from "./individalstaff";
import DisciplinaryForm from "./discipinaryform";
import ProbationForm from "./probation";
import ResignationForm from "./resignationform";
import { useContext } from "react";
import { AuthContext } from '../AuthContext';

export default function CreateAppraisal() {
  const locationState = useLocation();
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState({ id: "", Name: "" });
  const [type, setType] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [isFormCompleted, setIsFormCompleted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStaffDetailsMinimized, setIsStaffDetailsMinimized] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");

  const user = useContext(AuthContext);
  const requiresLocation = type !== "Staff Resignation";
  const completedSteps = [selected.id, type, ...(requiresLocation ? [location] : [])].filter(Boolean).length;
  const totalSteps = requiresLocation ? 3 : 2;

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const getStaff = async () => {
      const token = localStorage.getItem('token');

      const response = await fetch("https://api.insightos.co.uk/api/staff", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
      });

      const data = await response.json();
      const success = response.ok;
      const staff = data.data;

      if (!success) {
        console.error("Failed to fetch data");
        return;
      }

      const filter = staff.filter((person) => {
        if (user.access === "admin") {
          return person;
        } else {
          return person.department === user.Department && person.id !== user.id;
        }
      });

      setStaff(filter);
    };

    getStaff();
  }, []);

  useEffect(() => {
    const restoredDraft = locationState.state?.appraisalDraft;
    if (!restoredDraft) {
      return;
    }

    if (restoredDraft.selected) {
      setSelected(restoredDraft.selected);
    }

    if (restoredDraft.type) {
      setType(restoredDraft.type);
    }

    if (restoredDraft.location) {
      setLocation(restoredDraft.location);
    }

    setIsFormCompleted(false);
  }, [locationState.state?.heavyAiRefreshKey]);

  const restoredDraft = locationState.state?.appraisalDraft;
  const restoredAiTrainReport =
    locationState.state?.heavyAiJobResult?.report ||
    locationState.state?.heavyAiJobResult?.trainingReport ||
    null;

  const filteredStaff = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    if (!query) return staff;

    return staff.filter((person) => {
      const nameMatch = String(person?.Name || '').toLowerCase().includes(query);
      const roleMatch = String(person?.Role || '').toLowerCase().includes(query);
      const deptMatch = String(person?.department || '').toLowerCase().includes(query);
      return nameMatch || roleMatch || deptMatch;
    });
  }, [staff, staffSearch]);

  useEffect(() => {
    if (!isStaffModalOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isStaffModalOpen]);

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
  };

  // Track when the form is completed (all fields selected)
  useEffect(() => {
    setIsFormCompleted(Boolean(selected.id && type && (!requiresLocation || location)));
  }, [type, location, selected, requiresLocation]);

  useEffect(() => {
    if (!requiresLocation) {
      setLocation("");
    }
  }, [requiresLocation]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Appraisal':
        return '📋';
      case 'Disciplinary':
        return '⚠️';
      case 'Probation':
        return '🎯';
      case 'Monthly':
        return '📅';
      case 'Performance':
        return '📊';
      case 'Staff Resignation':
        return '👋';
      default:
        return '📄';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Appraisal':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Disciplinary':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'Probation':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Monthly':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'Performance':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'Staff Resignation':
        return 'text-rose-700 bg-rose-50 border-rose-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const glassPanelClass =
    'rounded-[28px] border border-white/45 bg-white/60 shadow-[0_20px_70px_rgba(15,23,42,0.12)] backdrop-blur-2xl';

  const glassCardClass =
    'rounded-[24px] border border-white/50 bg-white/65 shadow-[0_16px_45px_rgba(15,23,42,0.10)] backdrop-blur-2xl';

  const handleStaffSelect = (person) => {
    setSelected(person);
    setIsStaffModalOpen(false);
  };

  return (
    <>
      <StaffNav />

      {isStaffModalOpen && (
        <div className="fixed inset-0 z-[120]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setIsStaffModalOpen(false)}
            aria-label="Close staff picker"
          />

          <div className="absolute inset-x-0 bottom-0 top-auto sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-6">
            <div className="relative w-full rounded-t-[28px] border border-white/60 bg-white/95 p-5 shadow-2xl backdrop-blur-2xl sm:max-h-[88vh] sm:max-w-3xl sm:rounded-[28px] sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Select Staff Member</h3>
                  <p className="mt-1 text-sm text-slate-600">Choose who this report is for</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsStaffModalOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <div className="flex items-center rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <MagnifyingGlassIcon className="mr-2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    value={staffSearch}
                    onChange={(event) => setStaffSearch(event.target.value)}
                    placeholder="Search by name, role, or department"
                    className="w-full border-none bg-transparent text-sm text-slate-900 outline-none"
                  />
                </div>
              </div>

              <div className="max-h-[58vh] overflow-y-auto pr-1 sm:max-h-[60vh]">
                {filteredStaff.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
                    No staff members match your search.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredStaff.map((person) => {
                      const isActive = selected.id === person.id;
                      return (
                        <button
                          key={person.id}
                          type="button"
                          onClick={() => handleStaffSelect(person)}
                          className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                            isActive
                              ? 'border-indigo-300 bg-indigo-50/80'
                              : 'border-slate-200 bg-white hover:border-sky-300 hover:bg-sky-50/40'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-indigo-600 to-sky-500 text-sm font-semibold text-white">
                                {person.Name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                              <div>
                                <div className="font-semibold text-slate-900">{person.Name}</div>
                                <div className="text-xs text-slate-500">{person.Role || 'No role assigned'}</div>
                              </div>
                            </div>
                            {isActive && (
                              <span className="rounded-full bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-700">
                                Selected
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsStaffModalOpen(false)}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.22),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(168,85,247,0.18),_transparent_24%),linear-gradient(135deg,_#eef2ff_0%,_#f8fafc_45%,_#ecfeff_100%)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          
          {/* Header Section */}
          <div className="mb-8">
            <div className={`${glassPanelClass} p-6 sm:p-8`}>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-400 shadow-lg shadow-indigo-900/25">
                    <DocumentTextIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Create Staff Report</h1>
                  <p className="mt-1 text-slate-600">Generate professional appraisals, reviews, and resignation letters</p>
                </div>
              </div>
              
              {/* Progress Indicator */}
              {!isFormCompleted && (
                <div className="mt-6">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>Setup Progress</span>
                    <span>{completedSteps}/{totalSteps} Complete</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-white/80">
                    <div 
                      className="h-2 rounded-full bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-400 transition-all duration-300"
                      style={{ width: `${(completedSteps / totalSteps) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content Grid */}
          <div className={`grid gap-8 ${
            isMobile 
              ? 'grid-cols-1 gap-6' 
              : isFormCompleted && isStaffDetailsMinimized
                ? 'grid-cols-1'
                : isFormCompleted 
                  ? 'grid-cols-1 lg:grid-cols-2' 
                  : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            
            {/* Left Column - Setup or Staff Details */}
            <div className={`space-y-6 ${isFormCompleted && isStaffDetailsMinimized ? 'hidden' : ''}`}>
              {isFormCompleted ? (
                <div className={`${glassCardClass} overflow-hidden`}>
                  <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-400 px-6 py-4">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                      <UserIcon className="w-5 h-5 mr-2" />
                      Staff Information
                    </h2>
                    <button
                      onClick={() => setIsStaffDetailsMinimized(!isStaffDetailsMinimized)}
                      className="text-white hover:text-blue-100 transition-colors duration-200 p-1 rounded-lg hover:bg-white/10"
                      title={isStaffDetailsMinimized ? "Show staff details" : "Hide staff details"}
                    >
                      {isStaffDetailsMinimized ? (
                        <PlusIcon className="w-5 h-5" />
                      ) : (
                        <MinusIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <div className="p-6">
                    <StaffDetails staff={selected} />
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  
                  {/* Staff Selection */}
                  <div className={`${glassCardClass} p-6`}>
                    <div className="flex items-center mb-6">
                      <UserIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Select Staff Member</h3>
                    </div>

                    <label className="block text-sm font-medium text-gray-700 mb-2">Staff Member</label>
                    <button
                      type="button"
                      onClick={() => setIsStaffModalOpen(true)}
                      className="group w-full rounded-2xl border border-white/60 bg-white/90 p-4 text-left shadow-sm transition-colors duration-200 hover:border-sky-300 focus:border-indigo-500 focus:outline-none"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-sm font-medium text-white">
                            {selected.Name ? selected.Name.charAt(0).toUpperCase() : '?'}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-gray-900">
                              {selected.Name || 'Tap to choose a staff member'}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {selected.Role || 'Role will appear here after selection'}
                            </p>
                          </div>
                        </div>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 transition group-hover:bg-sky-100 group-hover:text-sky-700">
                          Browse
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Type Selection */}
                  <div className={`${glassCardClass} p-6`}>
                    <div className="flex items-center mb-6">
                      <DocumentTextIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Report Type</h3>
                    </div>
                    
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type of Assessment</label>
                    <select
                      id="type"
                      name="type"
                      value={type}
                      onChange={handleTypeChange}
                      className="block w-full rounded-2xl border border-white/60 bg-white/90 px-4 py-3 text-gray-900 placeholder-gray-500 shadow-sm transition-colors duration-200 hover:border-sky-300 focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="">Choose assessment type</option>
                      <option value="Appraisal">📋 Annual Appraisal</option>
                      <option value="Probation">🎯 Probation Review</option>
                      <option value="Monthly">📅 Monthly Check-in</option>
                      <option value="Performance">📊 Performance Review</option>
                      <option value="Disciplinary">⚠️ Disciplinary Action</option>
                      <option value="Staff Resignation">👋 Staff Resignation</option>
                    </select>
                    
                    {type && (
                      <div className={`mt-3 p-3 rounded-lg border ${getTypeColor(type)}`}>
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{getTypeIcon(type)}</span>
                          <span className="text-sm font-medium">
                            {type === 'Appraisal' && 'Comprehensive annual performance review'}
                            {type === 'Disciplinary' && 'Formal disciplinary procedure documentation'}
                            {type === 'Probation' && 'Probationary period assessment'}
                            {type === 'Monthly' && 'Regular monthly performance check'}
                            {type === 'Performance' && 'Performance improvement assessment'}
                            {type === 'Staff Resignation' && 'Generate a resignation acknowledgement letter and save leaving details'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Location Selection */}
                  {requiresLocation && <div className={`${glassCardClass} p-6`}>
                    <div className="flex items-center mb-6">
                      <MapPinIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Meeting Location</h3>
                    </div>
                    
                    <label className="block text-sm font-medium text-gray-700 mb-2">Where will this take place?</label>
                    <select
                      id="location"
                      name="location"
                      value={location}
                      onChange={handleLocationChange}
                      className="block w-full rounded-2xl border border-white/60 bg-white/90 px-4 py-3 text-gray-900 placeholder-gray-500 shadow-sm transition-colors duration-200 hover:border-sky-300 focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="">Select meeting format</option>
                      <option value="Meeting">🏢 Face to Face Meeting</option>
                      <option value="Virtual">💻 Virtual Meeting</option>
                      <option value="Hybrid">🔄 Hybrid Meeting</option>
                    </select>
                  </div>}
                </div>
              )}
            </div>

            {/* Right Column - Forms */}
            <div className={`space-y-6 ${isFormCompleted && isStaffDetailsMinimized ? 'col-span-full' : ''}`}>
              
              {/* Floating Staff Details Button (when minimized) */}
              {isFormCompleted && isStaffDetailsMinimized && (
                <div className={`fixed z-50 ${isMobile ? 'top-20 right-4' : 'top-24 left-6'}`}>
                  <button
                    onClick={() => setIsStaffDetailsMinimized(false)}
                    className="rounded-full border-2 border-white bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-400 p-3 text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                    title="Show staff details"
                  >
                    <div className="flex items-center space-x-2">
                      <UserIcon className="w-5 h-5" />
                      {!isMobile && (
                        <>
                          <span className="text-sm font-medium">{selected.Name}</span>
                          <PlusIcon className="w-4 h-4" />
                        </>
                      )}
                    </div>
                  </button>
                </div>
              )}

              {isFormCompleted && (
                <div className={`${glassCardClass} overflow-hidden`}>
                  <div className={`px-6 py-4 ${getTypeColor(type)} border-b flex items-center justify-between`}>
                    <div>
                      <h2 className="text-xl font-semibold flex items-center">
                        <span className="text-2xl mr-2">{getTypeIcon(type)}</span>
                        {type} Form
                      </h2>
                      <p className="text-sm opacity-80 mt-1">
                        Complete the assessment for {selected.Name}
                      </p>
                    </div>
                    {!isMobile && (
                      <div className="text-sm opacity-75">
                        {isStaffDetailsMinimized ? '📋 Full Screen Mode' : '📋 Split View'}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    {type === "Disciplinary" && (
                      <DisciplinaryForm
                        staffId={selected.id}
                        staffName={selected.Name}
                        role={selected.Role}
                        location={location}
                      />
                    )}

                    {(type === "Appraisal" || type === "Monthly" || type === "Performance") && (
                      <AppraisalForm
                        key={`${selected.id || 'unknown'}-${type || 'unknown'}-${location || 'unknown'}`}
                        staffId={selected.id}
                        staffName={selected.Name}
                        role={selected.Role}
                        location={location}
                        type={type}
                        initialFormData={restoredDraft?.formData || {}}
                        initialAITrainReport={restoredAiTrainReport}
                      />
                    )}

                    {type === "Probation" && (
                      <ProbationForm
                        staffId={selected.id}
                        staffName={selected.Name}
                        role={selected.Role}
                        location={location}
                        type={type}
                      />
                    )}

                    {type === "Staff Resignation" && (
                      <ResignationForm
                        staffId={selected.id}
                        staffName={selected.Name}
                        role={selected.Role}
                      />
                    )}

                  </div>
                </div>
              )}
              
              {!isFormCompleted && (
                <div className={`${glassCardClass} p-8 text-center`}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/80">
                    <DocumentTextIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to Begin</h3>
                  <p className="text-gray-600 mb-4">
                    Complete the setup form to generate your professional assessment
                  </p>
                  <div className="text-sm text-gray-500">
                    {!selected.id && "• Select a staff member"}
                    {!type && "• Choose assessment type"}
                    {requiresLocation && !location && "• Set meeting location"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
